// Data-fetching and messaging state for CallOffDetailModal.tsx, split out to
// keep CallOffDetailModal.tsx under the 500-line cap. Owns the call-off
// detail fetch (detail/timeline/edit-history/messages), the ASN tab count
// relay, and the message thread (send + mark-as-read). No behaviour change
// from the original inline state — moved verbatim.
import { useState, useEffect, useCallback } from 'react';
import { callOffAPI } from '../../../lib/api';
import type {
  CallOff, CallOffTimelineEvent, CallOffEditHistoryEntry, CallOffMessage,
} from '../../../lib/api';
import { toast } from 'sonner';

interface UseCallOffDetailModalDataArgs {
  open: boolean;
  callOffId: number | null;
  /** Whether the current user is on the customer side — used to scope which
   *  messages count as "unread" for the badge and the mark-as-read call. */
  isCustomer: boolean;
  /** Called after messages are marked read, so the caller's load list can
   *  drop its own unread flag (CR-7162). */
  onUpdated: () => void;
}

export interface UseCallOffDetailModalDataResult {
  callOff: CallOff | null;
  setCallOff: (callOff: CallOff | null) => void;
  timeline: CallOffTimelineEvent[];
  editHistory: CallOffEditHistoryEntry[];
  messages: CallOffMessage[];
  loading: boolean;
  asnTabCount: number | null;
  setAsnTabCount: (count: number | null) => void;
  fetchDetail: () => Promise<void>;
  newMessage: string;
  setNewMessage: (value: string) => void;
  sendingMessage: boolean;
  handleSendMessage: () => Promise<void>;
  markMessagesRead: () => Promise<void>;
  unreadCount: number;
}

/**
 * Loads and holds a call-off's detail/timeline/edit-history/messages, and
 * manages the message thread (send + mark-as-read).
 *
 * @param args.open - whether the modal is open
 * @param args.callOffId - the call-off id to load, or null when the modal has no target yet
 * @param args.isCustomer - whether the current user is on the customer side
 * @param args.onUpdated - called after messages are marked read
 * @returns call-off detail state plus fetch/message handlers for the modal
 */
export function useCallOffDetailModalData({
  open, callOffId, isCustomer, onUpdated,
}: UseCallOffDetailModalDataArgs): UseCallOffDetailModalDataResult {
  const [callOff, setCallOff] = useState<CallOff | null>(null);
  const [timeline, setTimeline] = useState<CallOffTimelineEvent[]>([]);
  const [editHistory, setEditHistory] = useState<CallOffEditHistoryEntry[]>([]);
  const [messages, setMessages] = useState<CallOffMessage[]>([]);
  const [loading, setLoading] = useState(false);
  // CR-7368: the live ASN count as reported by the ASN tab once it has loaded
  // its own list. Null until then, when the label falls back to the count that
  // came with the call-off detail payload. The tab's read is newer, so it wins.
  const [asnTabCount, setAsnTabCount] = useState<number | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!callOffId) return;
    setLoading(true);
    try {
      // CR-7732: the thread belongs to the two parties, so a haulier is
      // refused it. That must not take the whole call-off down with it —
      // everything else on this screen is theirs to read — so the messages
      // call is settled on its own and an empty thread is a valid answer.
      const [detailRes, timelineRes, historyRes, messagesRes] = await Promise.all([
        callOffAPI.getCallOff(callOffId),
        callOffAPI.getTimeline(callOffId),
        callOffAPI.getEditHistory(callOffId),
        callOffAPI.getMessages(callOffId).catch(() => null),
      ]);
      setCallOff(detailRes.data);
      setTimeline(timelineRes.data || []);
      setEditHistory(historyRes.data || []);
      setMessages(messagesRes?.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load call-off details';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [callOffId]);

  useEffect(() => {
    if (open && callOffId) {
      fetchDetail();
      setAsnTabCount(null);
    }
  }, [open, callOffId, fetchDetail]);

  const handleSendMessage = async () => {
    if (!callOffId || !newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const res = await callOffAPI.sendMessage(callOffId, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const markMessagesRead = useCallback(async () => {
    if (!callOffId) return;
    const unreadCount = messages.filter(m => !m.is_read && m.sender_role !== (isCustomer ? 'customer' : 'supplier')).length;
    if (unreadCount === 0) return;
    try {
      await callOffAPI.markMessagesRead(callOffId);
      setMessages(prev => prev.map(m => ({ ...m, is_read: true, read_at: new Date().toISOString() })));
      // CR-7162: the load list now carries an unread-message flag of its own,
      // read from the server. Without this the badge would still be showing
      // behind the modal after the thread had been read.
      onUpdated();
    } catch {
      // Non-critical
    }
  }, [callOffId, messages, isCustomer, onUpdated]);

  const unreadCount = messages.filter(m => !m.is_read && m.sender_role !== (isCustomer ? 'customer' : 'supplier')).length;

  return {
    callOff, setCallOff, timeline, editHistory, messages, loading,
    asnTabCount, setAsnTabCount, fetchDetail,
    newMessage, setNewMessage, sendingMessage, handleSendMessage,
    markMessagesRead, unreadCount,
  };
}
